import { createSelector } from '@reduxjs/toolkit';
import countBy from 'lodash/fp/countBy';
import getOr from 'lodash/fp/getOr';
import isEmpty from 'lodash/fp/isEmpty';
import type { CombinedAssetData, TransformedData } from 'src/services/types';

import {
    ACTIVITY_AVAILABLE,
    ACTIVITY_DRIVING,
    ACTIVITY_RESTING,
    ACTIVITY_WORKING,
    ASSET_TYPE_BUS,
    ASSET_TYPE_CAR,
    ASSET_TYPE_TRAILER,
    ASSET_TYPE_TRUCK,
    ASSET_TYPE_VAN,
} from '../../data/dataDefinition';
import { getData } from '../app/selectors';
import {
    getActivityFilter,
    getAssetFilter,
    getFilteredTransformedData,
    getNotificationFilter,
} from '../table/tableSelectors';
import type { Counts } from './Count';

export const hasActiveSummaryFilter = createSelector(
    getActivityFilter,
    getAssetFilter,
    getNotificationFilter,
    (activityFilter, assetFilter, notificationFilter) =>
        !isEmpty(activityFilter) || !isEmpty(assetFilter) || !isEmpty(notificationFilter)
);

export const hasTrucks = createSelector(
    getData,
    rawData => rawData && !isEmpty(rawData.filter(item => item.type === ASSET_TYPE_TRUCK))
);

export const hasBuses = createSelector(
    getData,
    rawData => rawData && !isEmpty(rawData.filter(item => item.type === ASSET_TYPE_BUS))
);

export const hasVans = createSelector(
    getData,
    rawData => rawData && !isEmpty(rawData.filter(item => item.type === ASSET_TYPE_VAN))
);

export const hasTrailers = createSelector(
    getData,
    rawData => rawData && !isEmpty(rawData.filter(item => item.type === ASSET_TYPE_TRAILER))
);

export const hasCars = createSelector(
    getData,
    rawData => rawData && !isEmpty(rawData.filter(item => item.type === ASSET_TYPE_CAR))
);

const getNotificationCounts = (data: CombinedAssetData[] | TransformedData[], counts: Counts) => {
    const exceptionsCountSource = 'notificationExceptions';
    const warningsCountSource = 'notificationWarnings';

    const result: Counts = { ...counts };

    for (const item of data) {
        result.exceptions += getOr(0, exceptionsCountSource, item);
        result.warnings += getOr(0, warningsCountSource, item);
    }

    return result;
};

const getActivityCounts = (data: CombinedAssetData[] | TransformedData[]) =>
    countBy(
        (item: CombinedAssetData | TransformedData) =>
            !isEmpty(item.currentActivity) ? item.currentActivity.toLowerCase() : item.currentActivity,
        data
    );

const getAssetTypeCounts = (data: CombinedAssetData[] | TransformedData[]) =>
    countBy((item: CombinedAssetData | TransformedData) => item.type, data);

const getDriverCount = (data: CombinedAssetData[] | TransformedData[]) =>
    countBy((item: CombinedAssetData | TransformedData) => !isEmpty(item.driverId), data).true;

const counts = {
    trucks: 0,
    vans: 0,
    buses: 0,
    trailer: 0,
    cars: 0,
    drivers: 0,
    exceptions: 0,
    warnings: 0,
    [ACTIVITY_WORKING]: 0,
    [ACTIVITY_AVAILABLE]: 0,
    [ACTIVITY_DRIVING]: 0,
    [ACTIVITY_RESTING]: 0,
};

const getValueOrZero = (value: number) => value ?? 0;

const getCounts = (data: CombinedAssetData[] | TransformedData[]) => {
    if (!data) {
        return counts;
    }

    const typeCounts = getAssetTypeCounts(data);
    const driverCount = getDriverCount(data);
    const activityCount = getActivityCounts(data);
    const notificationCounts = getNotificationCounts(data, counts);

    return {
        ...notificationCounts,
        trucks: getValueOrZero(typeCounts[ASSET_TYPE_TRUCK]),
        vans: getValueOrZero(typeCounts[ASSET_TYPE_VAN]),
        buses: getValueOrZero(typeCounts[ASSET_TYPE_BUS]),
        trailer: getValueOrZero(typeCounts[ASSET_TYPE_TRAILER]),
        cars: getValueOrZero(typeCounts[ASSET_TYPE_CAR]),
        drivers: getValueOrZero(driverCount),
        [ACTIVITY_WORKING]: getValueOrZero(activityCount[ACTIVITY_WORKING]),
        [ACTIVITY_AVAILABLE]: getValueOrZero(activityCount[ACTIVITY_AVAILABLE]),
        [ACTIVITY_DRIVING]: getValueOrZero(activityCount[ACTIVITY_DRIVING]),
        [ACTIVITY_RESTING]: getValueOrZero(activityCount[ACTIVITY_RESTING]),
    };
};

export const getTotalCounts = createSelector(getData, rawData => getCounts(rawData));

export const getSummaryCounts = createSelector(getFilteredTransformedData, data => getCounts(data));
